import ReportLead from '../shared/ReportLead';
import { useState, useEffect, useContext, useMemo, useCallback, useRef } from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    LinearProgress,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    Typography,
    useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ReactApexChart from 'react-apexcharts';
import { AppVarsContext } from '../../context/AppVarsContext';
import { applyTrendTooltip } from '../../system/apex-tooltip';
import { applyApexBrandGradient } from '../../system/apex-gradient';
import ReportQuestionAssistant from '../../system/controls/report-question-assistant';
import {
    getHeroChipSx,
    getMetricGridSx,
    getPlainSurfaceCardSx,
    getReportBackdropSx,
    getReportCanvasSx,
    getReportHeroSx,
    getReportTabsRailSx,
    getReportTabsSx,
    getSectionCardSx
} from '../shared/reportSurfaceStyles';
import { normalizeReportProfileVisibility, resolveShowCost } from '../../system/report-visibility';
import { formatUsd } from '../../system/report-formatters';

const INSTANT_ANALYSIS_VIEWS = [
    'Summary',
    'Cross-Channel',
    'Analysis'
];

const JOURNEY_LOADING_STAGES = [
    {
        title: 'Collecting performance',
        detail: 'Pulling connected channel, campaign, and conversion data for the selected date range.'
    },
    {
        title: 'Checking coverage',
        detail: 'Reviewing which platforms are active, which are thin, and where data gaps may affect the readout.'
    },
    {
        title: 'Ranking drivers',
        detail: 'Sorting the strongest programs, revenue drivers, and conversion signals so the highest-value opportunities surface first.'
    },
    {
        title: 'Writing insights',
        detail: 'Preparing a concise cross-channel narrative so the report is easier to act on quickly.'
    }
];

const formatSourceType = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    const labels = {
        adwords: 'Google Paid',
        bing: 'Bing',
        display: 'Programmatic',
        email: 'Email',
        lightbox: 'Lightbox',
        meta: 'Meta',
        website: 'Website'
    };
    if (labels[normalized]) return labels[normalized];
    if (!normalized) return 'Source';
    return normalized
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const fetchJsonOrThrow = async (url, options, fallbackMessage) => {
    const response = await fetch(url, options);
    const json = await response.json().catch(() => null);
    if (!response.ok) {
        throw new Error(json?.error || fallbackMessage || `Request failed: ${response.status}`);
    }
    return json;
};

const formatCompactNumber = (value) => new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1
}).format(Number(value || 0));

const formatNumber = (value) => new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0
}).format(Number(value || 0));

const formatCurrency = (value) => formatUsd(value);

const formatFixedPercent = (value) => `${Number(value || 0).toFixed(2)}%`;
const truncateText = (value, max = 36) => {
    const text = String(value || '');
    if (text.length <= max) return text;
    return `${text.slice(0, max - 3)}...`;
};

const getSafeNumber = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
};

const cleanInsightMessage = (value, fallback = 'Data unavailable') => {
    const text = String(value || '').trim();
    if (!text) return '';
    if (text === '[object Object]') return fallback;
    return text;
};

const ensureApexOptions = (options, chartType, theme, tooltipOptions = {}) => {
    const base = options && typeof options === 'object' ? options : {};
    const merged = applyApexBrandGradient({
        ...base,
        chart: {
            zoom: { enabled: false },
            toolbar: { show: false },
            ...(base.chart || {}),
            ...(chartType ? { type: chartType } : {})
        }
    }, theme, chartType);
    return applyTrendTooltip(theme, merged, tooltipOptions);
};

const normalizeChannelSummary = (summary) => ({
    impressions: getSafeNumber(summary?.impressions),
    clicks: getSafeNumber(summary?.clicks),
    conversions: getSafeNumber(summary?.conversions),
    revenue: getSafeNumber(summary?.revenue),
    cost: getSafeNumber(summary?.cost),
    ctr: getSafeNumber(summary?.ctr),
    cpc: getSafeNumber(summary?.cpc),
    cpm: getSafeNumber(summary?.cpm),
    convRate: getSafeNumber(summary?.conv_rate ?? summary?.convRate),
    avgGift: getSafeNumber(summary?.avg_gift ?? summary?.avgGift),
    roas: getSafeNumber(summary?.roas)
});

const hasSummarySignal = (summary) => (
    Object.values(summary || {}).some((value) => getSafeNumber(value) > 0)
);

const normalizeInsightRow = (row) => ({
    id: row?.id || row?.pkRecID || row?.name || 'row',
    name: row?.name || row?.label || 'Unknown',
    platform: row?.platform || 'Platform',
    channel: formatSourceType(row?.channel || row?.platform || ''),
    ...normalizeChannelSummary(row)
});

const normalizeInsightPlatform = (platform) => {
    const summary = normalizeChannelSummary(platform?.summary);
    const alerts = Array.isArray(platform?.alerts)
        ? platform.alerts.map((item) => cleanInsightMessage(item)).filter(Boolean)
        : [];
    const warnings = Array.isArray(platform?.warnings)
        ? platform.warnings.map((item) => cleanInsightMessage(item)).filter(Boolean)
        : [];
    const error = cleanInsightMessage(platform?.error);

    return {
        key: platform?.key || platform?.label || 'platform',
        label: platform?.label || formatSourceType(platform?.key),
        source: platform?.source || '',
        rowCount: getSafeNumber(platform?.row_count),
        summary,
        topRows: Array.isArray(platform?.top_rows) ? platform.top_rows.map(normalizeInsightRow) : [],
        alerts,
        warnings,
        error
    };
};

const normalizeAnalysisBlock = (analysis, key, fallbackTitle) => {
    const jsonBlock = analysis?.json?.[key];
    if (jsonBlock && typeof jsonBlock === 'object') {
        return {
            title: jsonBlock.title || fallbackTitle,
            summary: Array.isArray(jsonBlock.summary) ? jsonBlock.summary : [],
            insights: Array.isArray(jsonBlock.insights) ? jsonBlock.insights : [],
            concerns: Array.isArray(jsonBlock.concerns) ? jsonBlock.concerns : [],
            recommendations: Array.isArray(jsonBlock.recommendations) ? jsonBlock.recommendations : []
        };
    }

    const section = Array.isArray(analysis?.sections)
        ? analysis.sections.find((item) => item?.key === key)
        : null;

    return {
        title: section?.title || fallbackTitle,
        summary: Array.isArray(section?.bullets) ? section.bullets : [],
        insights: [],
        concerns: [],
        recommendations: []
    };
};

const AnalysisBulletGroup = ({ title, items, theme, tone = 'default' }) => {
    if (!Array.isArray(items) || !items.length) return null;

    const toneSx = {
        default: {
            borderColor: alpha(theme.palette.primary.main, 0.1),
            background: alpha(theme.palette.primary.main, 0.04)
        },
        warning: {
            borderColor: alpha(theme.palette.warning.main, 0.16),
            background: alpha(theme.palette.warning.main, 0.08)
        },
        success: {
            borderColor: alpha(theme.palette.success.main, 0.16),
            background: alpha(theme.palette.success.main, 0.08)
        }
    };

    return (
        <Box
            sx={{
                p: 1.2,
                borderRadius: 2.25,
                border: `1px solid ${toneSx[tone]?.borderColor || toneSx.default.borderColor}`,
                background: toneSx[tone]?.background || toneSx.default.background
            }}
        >
            <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700 }}>
                {title}
            </Typography>
            <Box component="ul" sx={{ pl: 2.2, my: 0.75 }}>
                {items.map((item, index) => (
                    <Box key={`${title}-${index}`} component="li" sx={{ mt: index === 0 ? 0 : 0.55 }}>
                        <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
                            {item}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

function TabPanel({ children, value, index }) {
    if (value !== index) return null;
    return <Box sx={{ display: 'grid', gap: 1.5 }}>{children}</Box>;
}

const InstantAnalysis = ({ setLoading: setExternalLoading, title = 'ReportWerks Instant Analysis' }) => {
    const rid = sessionStorage.getItem('rid');
    const { appVars, appLoading } = useContext(AppVarsContext);
    const theme = useTheme();
    const [client, setClient] = useState(null);
    const [reportProfile, setReportProfile] = useState(null);
    const [journeyData, setJourneyData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [tabIndex, setTabIndex] = useState(0);
    const instantAssistantConfig = useMemo(() => ({
        activeView: INSTANT_ANALYSIS_VIEWS[tabIndex] || 'Summary'
    }), [tabIndex]);
    const [selectedDateS, setSelectedDateS] = useState(sessionStorage.getItem('sDate') || '');
    const [selectedDateE, setSelectedDateE] = useState(sessionStorage.getItem('eDate') || '');
    const [journeyElapsedMs, setJourneyElapsedMs] = useState(0);
    const journeyTimerRef = useRef(null);
    const journeyStartRef = useRef(null);

    const activeJourneyStage = Math.min(JOURNEY_LOADING_STAGES.length - 1, Math.floor(journeyElapsedMs / 2200));
    const journeyProgressValue = Math.min(18 + (activeJourneyStage * 24) + (((journeyElapsedMs % 2200) / 2200) * 14), 96);

    const pDate = useMemo(() => {
        if (!selectedDateS || !selectedDateE) return '';
        return new Intl.DateTimeFormat('en-US', { month: 'long', day: '2-digit', year: 'numeric', timeZone: 'UTC' }).format(new Date(selectedDateS))
            + " - "
            + new Intl.DateTimeFormat('en-US', { month: 'long', day: '2-digit', year: 'numeric', timeZone: 'UTC' }).format(new Date(selectedDateE));
    }, [selectedDateE, selectedDateS]);

    const fetchReport = useCallback(async () => {
        if (!appVars?.api_url || !rid) return;

        setExternalLoading?.(true);
        setLoading(true);
        setError('');
        setJourneyElapsedMs(0);
        journeyStartRef.current = Date.now();
        if (journeyTimerRef.current) {
            clearInterval(journeyTimerRef.current);
        }
        journeyTimerRef.current = setInterval(() => {
            if (!journeyStartRef.current) return;
            setJourneyElapsedMs(Date.now() - journeyStartRef.current);
        }, 100);

        try {
            const myHeaders = new Headers();
            myHeaders.append('Content-Type', 'application/json');

            const requestOptions = {
                method: 'POST',
                headers: myHeaders,
                body: JSON.stringify({
                    cid: rid,
                    sdate: sessionStorage.getItem('sDate'),
                    edate: sessionStorage.getItem('eDate')
                }),
                redirect: 'follow'
            };

            const [clientJson, profileJson, journeyJson] = await Promise.all([
                fetchJsonOrThrow(`${appVars.api_url}/tagmanager/${rid}`, undefined, 'Failed to load client context.'),
                fetchJsonOrThrow(`${appVars.api_url}/reportprofile/${rid}`, { method: 'GET', redirect: 'follow' }, 'Failed to load report profile.'),
                fetchJsonOrThrow(`${appVars.api_url}/journey/client`, requestOptions, 'Failed to load instant analysis data.')
            ]);

            setClient(clientJson);
            setReportProfile(normalizeReportProfileVisibility(profileJson));
            setJourneyData(journeyJson);
        } catch (nextError) {
            console.error(nextError);
            setError(nextError?.message || 'Failed to load instant analysis.');
            setJourneyData(null);
        } finally {
            setLoading(false);
            if (journeyTimerRef.current) {
                clearInterval(journeyTimerRef.current);
                journeyTimerRef.current = null;
            }
            setExternalLoading?.(false);
        }
    }, [appVars?.api_url, rid, setExternalLoading]);

    useEffect(() => {
        if (appLoading) return;
        if (!rid) {
            setLoading(false);
            setError('No report session found for instant analysis.');
            setExternalLoading?.(false);
            return;
        }
        fetchReport();
    }, [appLoading, fetchReport, rid, setExternalLoading]);

    useEffect(() => () => {
        if (journeyTimerRef.current) {
            clearInterval(journeyTimerRef.current);
            journeyTimerRef.current = null;
        }
    }, []);

    useEffect(() => {
        const handleStorageChange = () => {
            setSelectedDateS(sessionStorage.getItem('sDate') || '');
            setSelectedDateE(sessionStorage.getItem('eDate') || '');
            setTimeout(() => {
                fetchReport();
            }, 100);
        };

        window.addEventListener('dc', handleStorageChange);
        return () => window.removeEventListener('dc', handleStorageChange);
    }, [fetchReport]);

    const settings = useMemo(() => {
        if (!reportProfile?.settings) return {};
        if (typeof reportProfile.settings === 'object') return reportProfile.settings;
        try {
            return JSON.parse(reportProfile.settings);
        } catch (nextError) {
            console.error('Invalid report settings', nextError);
            return {};
        }
    }, [reportProfile?.settings]);
    const showCost = resolveShowCost(reportProfile);

    const instantAnalysisEnabled = settings?.ad_sets?.instant_analysis?.id === 1;

    const crossChannelInsights = useMemo(() => {
        const payload = journeyData?.channel_insights || {};
        const totals = normalizeChannelSummary(payload?.totals);
        const platforms = Array.isArray(payload?.platforms) ? payload.platforms.map(normalizeInsightPlatform) : [];
        const activePlatforms = platforms.filter((platform) => platform.rowCount > 0 || hasSummarySignal(platform.summary));
        const inactivePlatforms = platforms.filter((platform) => !activePlatforms.includes(platform));
        const topPerformers = Array.isArray(payload?.top_performers) ? payload.top_performers.map(normalizeInsightRow) : [];
        const warnings = Array.isArray(payload?.warnings)
            ? payload.warnings.map((item) => cleanInsightMessage(item)).filter(Boolean)
            : [];
        const negativeFindings = Array.isArray(payload?.negative_findings)
            ? payload.negative_findings.map((item) => cleanInsightMessage(item)).filter(Boolean)
            : [];

        return {
            totals,
            platforms,
            activePlatforms,
            inactivePlatforms,
            topPerformers,
            warnings,
            negativeFindings,
            hasData:
                hasSummarySignal(totals)
                || activePlatforms.length > 0
                || topPerformers.length > 0
                || warnings.length > 0
                || negativeFindings.length > 0
        };
    }, [journeyData?.channel_insights]);

    const crossChannelAnalysisBlock = useMemo(
        () => normalizeAnalysisBlock(journeyData?.analysis, 'cross_channel', 'Cross-Channel Performance Analysis'),
        [journeyData?.analysis]
    );
    const summaryInsightCards = useMemo(() => {
        const topPerformer = crossChannelInsights.topPerformers[0];
        const topPlatform = crossChannelInsights.activePlatforms[0];
        const connectionIssue = crossChannelInsights.warnings[0] || crossChannelAnalysisBlock.concerns[0];

        return [
            {
                eyebrow: 'Top program',
                value: topPerformer?.name || 'No program signal',
                note: topPerformer
                    ? `${formatCurrency(topPerformer.revenue)} revenue and ${formatNumber(topPerformer.conversions)} conversions led the range.`
                    : 'No performer-level ranking was returned.'
            },
            {
                eyebrow: 'Active coverage',
                value: `${crossChannelInsights.activePlatforms.length} ${crossChannelInsights.activePlatforms.length === 1 ? 'channel' : 'channels'}`,
                note: crossChannelInsights.inactivePlatforms.length
                    ? `${crossChannelInsights.inactivePlatforms.length} channel connections are unavailable or contributed no usable rows.`
                    : 'All connected channels with usable rows are contributing to this readout.'
            },
            {
                eyebrow: 'Top channel',
                value: topPlatform?.label || 'No active channel',
                note: topPlatform
                    ? `${formatNumber(topPlatform.rowCount)} rows and ${formatCurrency(topPlatform.summary.revenue)} revenue are visible here.`
                    : 'No channel summary is populated yet.'
            },
            {
                eyebrow: 'Primary risk',
                value: connectionIssue ? 'Coverage gap' : 'Stable coverage',
                note: connectionIssue || 'No connection warnings or major cross-channel concerns were returned.'
            }
        ];
    }, [crossChannelAnalysisBlock.concerns, crossChannelInsights]);
    const summaryActionPriorities = useMemo(() => {
        const priorities = [];
        const topPerformer = crossChannelInsights.topPerformers[0];
        const activeChannel = crossChannelInsights.activePlatforms[0];
        const connectionIssue = crossChannelInsights.warnings[0] || crossChannelAnalysisBlock.concerns[0];
        const channelRecommendation = crossChannelAnalysisBlock.recommendations[0];

        if (activeChannel) {
            priorities.push({
                title: 'Protect the strongest channel signal',
                body: `${activeChannel.label} is the clearest connected channel in this range with ${formatCurrency(activeChannel.summary.revenue)} in revenue and ${formatNumber(activeChannel.summary.conversions)} conversions. Use it as the baseline for audience, landing-page, and creative comparisons.`
            });
        }

        if (topPerformer) {
            priorities.push({
                title: `Scale what is already converting`,
                body: showCost
                    ? `${topPerformer.name} is the current leader with ${formatCurrency(topPerformer.revenue)} in revenue, ${formatNumber(topPerformer.conversions)} conversions, and ${topPerformer.roas.toFixed(2)}x ROAS. Use it as the benchmark for budget, audience, and creative decisions.`
                    : `${topPerformer.name} is the current leader with ${formatCurrency(topPerformer.revenue)} in revenue and ${formatNumber(topPerformer.conversions)} conversions. Use it as the benchmark for audience, creative, and landing-page decisions.`
            });
        }

        priorities.push({
            title: 'Fix visibility gaps before optimizing deeper',
            body: connectionIssue
                || 'Close any missing platform or attribution gaps so the analysis reflects the full media mix.'
        });

        priorities.push({
            title: 'Turn the analysis into the next test',
            body: channelRecommendation
                || 'Pick one change from the strongest insight and turn it into the next controlled test.'
        });

        return priorities.filter((item) => item?.body);
    }, [
        crossChannelAnalysisBlock.concerns,
        crossChannelAnalysisBlock.recommendations,
        crossChannelInsights.activePlatforms,
        crossChannelInsights.topPerformers,
        crossChannelInsights.warnings,
        showCost
    ]);

    const crossChannelTopPerformers = useMemo(
        () => crossChannelInsights.topPerformers.slice(0, 6),
        [crossChannelInsights.topPerformers]
    );
    const performerRevenueChartOptions = useMemo(() => ensureApexOptions({
        chart: { type: 'bar' },
        plotOptions: { bar: { horizontal: true, borderRadius: 6, barHeight: '55%' } },
        dataLabels: { enabled: false },
        colors: [theme.palette.primary.main],
        xaxis: {
            categories: crossChannelTopPerformers.map((item) => truncateText(item.name, 42))
        }
    }, 'bar', theme, {
        valueFormatter: (value) => formatCurrency(value)
    }), [crossChannelTopPerformers, theme]);
    const performerConversionChartOptions = useMemo(() => ensureApexOptions({
        chart: { type: 'bar' },
        plotOptions: { bar: { horizontal: true, borderRadius: 6, barHeight: '55%' } },
        dataLabels: { enabled: false },
        colors: [theme.palette.secondary.main],
        xaxis: {
            categories: crossChannelTopPerformers.map((item) => truncateText(item.name, 42))
        }
    }, 'bar', theme, {
        valueFormatter: (value) => formatNumber(value)
    }), [crossChannelTopPerformers, theme]);
    const performerRevenueSeries = useMemo(
        () => [{ name: 'Revenue', data: crossChannelTopPerformers.map((item) => item.revenue) }],
        [crossChannelTopPerformers]
    );
    const performerConversionSeries = useMemo(
        () => [{ name: 'Conversions', data: crossChannelTopPerformers.map((item) => item.conversions) }],
        [crossChannelTopPerformers]
    );

    const reportCanvasSx = getReportCanvasSx(theme);
    const reportTabsRailSx = getReportTabsRailSx(theme);
    const reportTabsSx = getReportTabsSx(theme);
    const heroSx = getReportHeroSx(theme);
    const sectionCardSx = getSectionCardSx(theme);
    const plainCardSx = getPlainSurfaceCardSx(theme);
    const metricGridSx = getMetricGridSx(theme);

    return (
        <Box sx={{ width: '100%', p: 0 }}>
            <Box sx={reportCanvasSx}>
                <Box sx={getReportBackdropSx(theme)} />
                <Box sx={{ position: 'relative', display: 'grid', gap: 2 }}>
                    <Box sx={heroSx}>
                        <Box sx={{ maxWidth: 920 }}>
                            <ReportLead eyebrow="Instant Analysis" title="Turn performance into action" description={`Read the selected date range for ${client?.name || client?.clientname || 'this client'} and explore the strongest channel, program, and conversion signals.`} />
                        </Box>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ justifyContent: 'flex-end' }}>
                            {pDate ? <Chip label={pDate} sx={getHeroChipSx(theme)} /> : null}
                            {crossChannelInsights.activePlatforms.length ? (
                                <Chip
                                    label={`${crossChannelInsights.activePlatforms.length} active ${crossChannelInsights.activePlatforms.length === 1 ? 'channel' : 'channels'}`}
                                    sx={getHeroChipSx(theme)}
                                />
                            ) : null}
                            <Chip
                                label={instantAnalysisEnabled ? 'Enabled in reporting' : 'Disabled in reporting'}
                                sx={{
                                    ...getHeroChipSx(theme),
                                    bgcolor: instantAnalysisEnabled ? alpha(theme.palette.success.main, 0.12) : alpha(theme.palette.warning.main, 0.12),
                                    color: instantAnalysisEnabled ? theme.palette.success.dark : theme.palette.warning.dark,
                                    borderColor: instantAnalysisEnabled ? alpha(theme.palette.success.main, 0.22) : alpha(theme.palette.warning.main, 0.22)
                                }}
                            />
                        </Stack>
                    </Box>

                    {!instantAnalysisEnabled ? (
                        <Alert severity="info" sx={plainCardSx}>
                            Instant Analysis is currently turned off for this client in reporting settings.
                        </Alert>
                    ) : null}

                    {loading ? (
                        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.05fr) minmax(320px, 0.95fr)' } }}>
                            <Box
                                sx={{
                                    ...plainCardSx,
                                    p: { xs: 2, md: 2.5 },
                                    background: `linear-gradient(160deg, ${theme.palette.primary.dark || theme.palette.primary.main} 0%, ${theme.palette.primary.main} 52%, ${theme.palette.secondary.main} 100%)`,
                                    color: '#ffffff'
                                }}
                            >
                                <Stack spacing={2}>
                                    <Chip
                                        label="Building report"
                                        sx={{
                                            width: 'fit-content',
                                            fontWeight: 700,
                                            borderRadius: 999,
                                            bgcolor: alpha('#ffffff', 0.16),
                                            color: '#ffffff'
                                        }}
                                    />
                                    <Box>
                                        <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 0.98 }}>
                                            Turning raw activity into a client-ready instant analysis
                                        </Typography>
                                        <Typography sx={{ mt: 1.1, color: alpha('#ffffff', 0.86), maxWidth: 620 }}>
                                            This step is collecting channel performance, ranking the strongest drivers, and drafting the narrative you can use in client conversations.
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'grid', gap: 0.8 }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={journeyProgressValue}
                                            sx={{
                                                height: 10,
                                                borderRadius: 999,
                                                bgcolor: alpha('#ffffff', 0.12),
                                                '& .MuiLinearProgress-bar': {
                                                    borderRadius: 999,
                                                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
                                                }
                                            }}
                                        />
                                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                            <Chip
                                                label={`Elapsed ${(journeyElapsedMs / 1000).toFixed(1)}s`}
                                                sx={{
                                                    fontWeight: 700,
                                                    borderRadius: 999,
                                                    bgcolor: alpha('#ffffff', 0.14),
                                                    color: '#ffffff'
                                                }}
                                            />
                                            <Chip
                                                label={JOURNEY_LOADING_STAGES[activeJourneyStage]?.title || 'Processing'}
                                                sx={{
                                                    fontWeight: 700,
                                                    borderRadius: 999,
                                                    bgcolor: alpha('#ffffff', 0.14),
                                                    color: '#ffffff'
                                                }}
                                            />
                                        </Stack>
                                    </Box>
                                    <Box sx={{ display: 'grid', gap: 0.9, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0, 1fr))' }, ...metricGridSx }}>
                                        {Array.from({ length: 4 }).map((_, index) => (
                                            <Card key={`metric-skeleton-${index}`}>
                                                <CardContent>
                                                    <Skeleton variant="text" width="55%" />
                                                    <Skeleton variant="text" width="35%" height={42} />
                                                    <Skeleton variant="text" width="70%" />
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </Box>
                                </Stack>
                            </Box>

                            <Box sx={{ display: 'grid', gap: 1 }}>
                                {JOURNEY_LOADING_STAGES.map((stage, index) => {
                                    const state = index < activeJourneyStage ? 'done' : index === activeJourneyStage ? 'active' : 'pending';
                                    return (
                                        <Box
                                            key={stage.title}
                                            sx={{
                                                ...plainCardSx,
                                                p: 1.35,
                                                border: `1px solid ${state === 'active'
                                                    ? alpha(theme.palette.primary.main, 0.28)
                                                    : alpha(theme.palette.primary.main, 0.14)}`,
                                                background: state === 'active'
                                                    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`
                                                    : alpha('#ffffff', state === 'done' ? 0.88 : 0.74),
                                                opacity: state === 'pending' ? 0.7 : 1
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', gap: 1.1, alignItems: 'flex-start' }}>
                                                <Box
                                                    sx={{
                                                        width: 34,
                                                        height: 34,
                                                        borderRadius: 2,
                                                        display: 'grid',
                                                        placeItems: 'center',
                                                        flexShrink: 0,
                                                        fontWeight: 800,
                                                        color: state === 'active' ? '#ffffff' : theme.palette.primary.dark,
                                                        background: state === 'active'
                                                            ? theme.palette.primary.main
                                                            : alpha(theme.palette.primary.main, 0.12)
                                                    }}
                                                >
                                                    {index + 1}
                                                </Box>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                        {stage.title}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4, lineHeight: 1.6 }}>
                                                        {stage.detail}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    ) : null}

                    {!loading && error ? (
                        <Alert severity="error" sx={plainCardSx}>
                            {error}
                        </Alert>
                    ) : null}

                    {!loading && !error && !journeyData ? (
                        <Alert severity="info" sx={plainCardSx}>
                            No instant analysis data was returned for the selected range.
                        </Alert>
                    ) : null}

                    {!loading && !error && journeyData ? (
                        <>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1.25, ...metricGridSx }}>
                                {[
                                    { label: 'Impressions', value: formatCompactNumber(crossChannelInsights.totals.impressions), note: 'Connected delivery in range' },
                                    { label: 'Conversions', value: formatNumber(crossChannelInsights.totals.conversions), note: `${formatFixedPercent(crossChannelInsights.totals.convRate)} conv rate` },
                                    { label: 'Revenue', value: formatCurrency(crossChannelInsights.totals.revenue), note: `${formatCurrency(crossChannelInsights.totals.avgGift)} avg gift` },
                                    { label: 'Active Channels', value: crossChannelInsights.activePlatforms.length, note: `${crossChannelInsights.inactivePlatforms.length} unavailable or unconnected` }
                                ].map((item) => (
                                    <Card key={item.label}>
                                        <CardContent>
                                            <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700 }}>
                                                {item.label}
                                            </Typography>
                                            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.03em', mt: 0.6, wordBreak: 'break-word' }}>
                                                {item.value}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45 }}>
                                                {item.note}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Box>

                            <Box sx={reportTabsRailSx}>
                                <Tabs value={tabIndex} onChange={(event, nextValue) => setTabIndex(nextValue)} sx={{ ...reportTabsSx, flex: 1, minWidth: 0 }}>
                                    <Tab label="Summary" />
                                    <Tab label="Cross-Channel" />
                                    <Tab label="Analysis" />
                                </Tabs>
                            </Box>

                            <TabPanel value={tabIndex} index={0}>
                                <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', xl: 'minmax(340px, 0.9fr) minmax(0, 1.1fr)' } }}>
                                    <Box sx={{ ...plainCardSx, p: 1.5, display: 'grid', gap: 1.1, background: `linear-gradient(160deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)` }}>
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                                                What stands out
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                Use these points to explain what is working, where coverage is thin, and where the strongest conversion signals are coming from.
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'grid', gap: 1 }}>
                                            {summaryInsightCards.map((item) => (
                                                <Box
                                                    key={item.eyebrow}
                                                    sx={{
                                                        p: 1.15,
                                                        borderRadius: 2.5,
                                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                                                        background: alpha('#ffffff', 0.8),
                                                        display: 'grid',
                                                        gap: 0.35
                                                    }}
                                                >
                                                    <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700 }}>
                                                        {item.eyebrow}
                                                    </Typography>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2, wordBreak: 'break-word' }}>
                                                        {item.value}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {item.note}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>

                                    <Card sx={sectionCardSx}>
                                        <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.1 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Action priorities</Typography>
                                                <Chip label="Client-ready" size="small" sx={getHeroChipSx(theme)} />
                                            </Box>
                                            <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>
                                                {summaryActionPriorities.map((item, index) => (
                                                    <Box
                                                        key={item.title}
                                                        sx={{
                                                            p: 1.2,
                                                            borderRadius: 2.5,
                                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                                            background: alpha(theme.palette.primary.main, 0.04),
                                                            display: 'grid',
                                                            gap: 0.55,
                                                            alignContent: 'start'
                                                        }}
                                                    >
                                                        <Chip
                                                            size="small"
                                                            label={`Step ${index + 1}`}
                                                            sx={{
                                                                width: 'fit-content',
                                                                fontWeight: 700,
                                                                bgcolor: alpha(theme.palette.primary.main, 0.12),
                                                                color: theme.palette.primary.dark
                                                            }}
                                                        />
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                                            {item.title}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {item.body}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Box>

                                <Box sx={{ display: 'grid', gap: 1.25 }}>
                                    <Card sx={sectionCardSx}>
                                        <CardContent sx={{ p: { xs: 1.5, md: 2 }, display: 'grid', gap: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                                <Box>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                                                        {crossChannelAnalysisBlock.title || 'Cross-Channel Performance Analysis'}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                                                        The paid-media readout, channel blind spots, and where the next budget or creative changes should come from.
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    label={`${crossChannelInsights.activePlatforms.length} active ${crossChannelInsights.activePlatforms.length === 1 ? 'channel' : 'channels'}`}
                                                    size="small"
                                                    sx={getHeroChipSx(theme)}
                                                />
                                            </Box>
                                            {crossChannelInsights.hasData ? (
                                                <>
                                                    <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0, 1fr))' } }}>
                                                        {[
                                                            {
                                                                label: 'Impressions',
                                                                value: formatCompactNumber(crossChannelInsights.totals.impressions),
                                                                note: 'Connected delivery'
                                                            },
                                                            {
                                                                label: 'Conversions',
                                                                value: formatNumber(crossChannelInsights.totals.conversions),
                                                                note: `${formatFixedPercent(crossChannelInsights.totals.convRate)} conv rate`
                                                            },
                                                            {
                                                                label: 'Revenue',
                                                                value: formatCurrency(crossChannelInsights.totals.revenue),
                                                                note: `${crossChannelInsights.totals.roas.toFixed(2)}x ROAS`
                                                            },
                                                            {
                                                                label: 'Top Program',
                                                                value: truncateText(crossChannelInsights.topPerformers[0]?.name || 'No program signal', 24),
                                                                note: crossChannelInsights.topPerformers[0]
                                                                    ? `${formatCurrency(crossChannelInsights.topPerformers[0].revenue)} revenue`
                                                                    : 'No winning program yet'
                                                            }
                                                        ].map((item) => (
                                                            <Box
                                                                key={item.label}
                                                                sx={{
                                                                    p: 1.1,
                                                                    borderRadius: 2.25,
                                                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                                                    background: alpha(theme.palette.primary.main, 0.04),
                                                                    display: 'grid',
                                                                    gap: 0.3
                                                                }}
                                                            >
                                                                <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700 }}>
                                                                    {item.label}
                                                                </Typography>
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2, wordBreak: 'break-word' }}>
                                                                    {item.value}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {item.note}
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                    <AnalysisBulletGroup title="Summary" items={crossChannelAnalysisBlock.summary} theme={theme} />
                                                    <AnalysisBulletGroup title="Insights" items={crossChannelAnalysisBlock.insights} theme={theme} tone="success" />
                                                    <AnalysisBulletGroup title="Concerns" items={[...crossChannelAnalysisBlock.concerns, ...crossChannelInsights.negativeFindings]} theme={theme} tone="warning" />
                                                    <AnalysisBulletGroup title="Recommendations" items={crossChannelAnalysisBlock.recommendations} theme={theme} />
                                                    <AnalysisBulletGroup title="Connection Warnings" items={crossChannelInsights.warnings} theme={theme} tone="warning" />
                                                </>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    No cross-channel performance data was returned for this range.
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Box>
                            </TabPanel>

                            <TabPanel value={tabIndex} index={1}>
                                {crossChannelInsights.hasData ? (
                                    <Box sx={{ display: 'grid', gap: 1.25 }}>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(6, minmax(0, 1fr))' }, gap: 1.25, ...metricGridSx }}>
                                            {[
                                                { label: 'Impressions', value: formatCompactNumber(crossChannelInsights.totals.impressions), note: 'Paid-media delivery' },
                                                { label: 'Clicks', value: formatNumber(crossChannelInsights.totals.clicks), note: `${formatFixedPercent(crossChannelInsights.totals.ctr)} CTR` },
                                                { label: 'Conversions', value: formatNumber(crossChannelInsights.totals.conversions), note: `${formatFixedPercent(crossChannelInsights.totals.convRate)} conv rate` },
                                                { label: 'Revenue', value: formatCurrency(crossChannelInsights.totals.revenue), note: `${formatCurrency(crossChannelInsights.totals.avgGift)} avg gift` },
                                                ...(showCost ? [{ label: 'ROAS', value: `${crossChannelInsights.totals.roas.toFixed(2)}x`, note: `${formatCurrency(crossChannelInsights.totals.cost)} spend` }] : []),
                                                { label: 'Active Channels', value: crossChannelInsights.activePlatforms.length, note: `${crossChannelInsights.inactivePlatforms.length} unavailable or unconnected` }
                                            ].map((item) => (
                                                <Card key={item.label}>
                                                    <CardContent>
                                                        <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700 }}>
                                                            {item.label}
                                                        </Typography>
                                                        <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.03em', mt: 0.55 }}>
                                                            {item.value}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45 }}>
                                                            {item.note}
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            ))}
                                        </Box>

                                        <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.05fr) minmax(340px, 0.95fr)' } }}>
                                            <Card sx={sectionCardSx}>
                                                <CardContent sx={{ p: { xs: 1.5, md: 2 }, display: 'grid', gap: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Platform coverage</Typography>
                                                        <Chip label={`${crossChannelInsights.platforms.length} connections`} size="small" sx={getHeroChipSx(theme)} />
                                                    </Box>
                                                    <Box sx={{ display: 'grid', gap: 1 }}>
                                                        {crossChannelInsights.platforms.map((platform) => {
                                                            const isActive = platform.rowCount > 0 || hasSummarySignal(platform.summary);
                                                            const primaryMessage = platform.error
                                                                || platform.alerts[0]
                                                                || platform.warnings[0]
                                                                || `${formatNumber(platform.rowCount)} rows contributing to this readout.`;
                                                            return (
                                                                <Box
                                                                    key={platform.key}
                                                                    sx={{
                                                                        p: 1.2,
                                                                        borderRadius: 2.5,
                                                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                                                        background: alpha(isActive ? theme.palette.primary.main : theme.palette.warning.main, isActive ? 0.04 : 0.08),
                                                                        display: 'grid',
                                                                        gap: 0.75
                                                                    }}
                                                                >
                                                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                                                                        <Box sx={{ minWidth: 0 }}>
                                                                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                                                                {platform.label}
                                                                            </Typography>
                                                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                                                                {primaryMessage}
                                                                            </Typography>
                                                                        </Box>
                                                                        <Chip
                                                                            size="small"
                                                                            label={isActive ? 'Active' : 'Unavailable'}
                                                                            sx={{
                                                                                fontWeight: 700,
                                                                                bgcolor: isActive ? alpha(theme.palette.success.main, 0.12) : alpha(theme.palette.warning.main, 0.16),
                                                                                color: isActive ? theme.palette.success.dark : theme.palette.warning.dark
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                    <Box sx={{ display: 'grid', gap: 0.35, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0, 1fr))' } }}>
                                                                        {[
                                                                            { label: 'Impressions', value: formatCompactNumber(platform.summary.impressions) },
                                                                            { label: 'Clicks', value: formatNumber(platform.summary.clicks) },
                                                                            { label: 'Conversions', value: formatNumber(platform.summary.conversions) },
                                                                            { label: 'Revenue', value: formatCurrency(platform.summary.revenue) }
                                                                        ].map((metric) => (
                                                                            <Box key={`${platform.key}-${metric.label}`}>
                                                                                <Typography variant="caption" color="text.secondary">{metric.label}</Typography>
                                                                                <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.2 }}>{metric.value}</Typography>
                                                                            </Box>
                                                                        ))}
                                                                    </Box>
                                                                </Box>
                                                            );
                                                        })}
                                                    </Box>
                                                </CardContent>
                                            </Card>

                                            <Card sx={sectionCardSx}>
                                                <CardContent sx={{ p: { xs: 1.5, md: 2 }, display: 'grid', gap: 1 }}>
                                                    <Box>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                                                            {crossChannelAnalysisBlock.title || 'Cross-channel readout'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                                                            Use this section to explain channel contribution, data gaps, and where optimization should happen next.
                                                        </Typography>
                                                    </Box>
                                                    <AnalysisBulletGroup title="Summary" items={crossChannelAnalysisBlock.summary} theme={theme} />
                                                    <AnalysisBulletGroup title="Insights" items={crossChannelAnalysisBlock.insights} theme={theme} tone="success" />
                                                    <AnalysisBulletGroup title="Concerns" items={[...crossChannelAnalysisBlock.concerns, ...crossChannelInsights.negativeFindings]} theme={theme} tone="warning" />
                                                    <AnalysisBulletGroup title="Recommendations" items={crossChannelAnalysisBlock.recommendations} theme={theme} />
                                                    <AnalysisBulletGroup title="Connection Warnings" items={crossChannelInsights.warnings} theme={theme} tone="warning" />
                                                </CardContent>
                                            </Card>
                                        </Box>

                                        <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' } }}>
                                            <Card sx={sectionCardSx}>
                                                <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                                                        Top Programs by Revenue
                                                    </Typography>
                                                    {crossChannelTopPerformers.length ? (
                                                        <ReactApexChart
                                                            options={performerRevenueChartOptions}
                                                            series={performerRevenueSeries}
                                                            type="bar"
                                                            height={320}
                                                        />
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            No performer-level revenue rows were returned.
                                                        </Typography>
                                                    )}
                                                </CardContent>
                                            </Card>
                                            <Card sx={sectionCardSx}>
                                                <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                                                        Top Programs by Conversions
                                                    </Typography>
                                                    {crossChannelTopPerformers.length ? (
                                                        <ReactApexChart
                                                            options={performerConversionChartOptions}
                                                            series={performerConversionSeries}
                                                            type="bar"
                                                            height={320}
                                                        />
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            No performer-level conversion rows were returned.
                                                        </Typography>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Box>

                                        {crossChannelTopPerformers.length ? (
                                            <Card sx={sectionCardSx}>
                                                <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.1 }}>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Top performers</Typography>
                                                        <Chip label={`${crossChannelTopPerformers.length} programs`} size="small" sx={getHeroChipSx(theme)} />
                                                    </Box>
                                                    <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' } }}>
                                                        {crossChannelTopPerformers.map((item, index) => (
                                                            <Box
                                                                key={`${item.id}-${index}`}
                                                                sx={{
                                                                    p: 1.2,
                                                                    borderRadius: 2.5,
                                                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                                                    background: alpha(theme.palette.primary.main, 0.04),
                                                                    display: 'grid',
                                                                    gap: 0.75
                                                                }}
                                                            >
                                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                                                                    <Box sx={{ minWidth: 0 }}>
                                                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, wordBreak: 'break-word' }}>
                                                                            {item.name}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                                                            {item.platform} • {item.channel}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Chip
                                                                        size="small"
                                                                        label={`#${index + 1}`}
                                                                        sx={{
                                                                            fontWeight: 700,
                                                                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                                                                            color: theme.palette.primary.dark
                                                                        }}
                                                                    />
                                                                </Box>
                                                                <Box sx={{ display: 'grid', gap: 0.35, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0, 1fr))' } }}>
                                                                    {[
                                                                        { label: 'Revenue', value: formatCurrency(item.revenue) },
                                                                        { label: 'Conversions', value: formatNumber(item.conversions) },
                                                                        ...(showCost ? [{ label: 'ROAS', value: `${item.roas.toFixed(2)}x` }] : []),
                                                                        { label: 'Conv Rate', value: formatFixedPercent(item.convRate) }
                                                                    ].map((metric) => (
                                                                        <Box key={`${item.id}-${metric.label}`}>
                                                                            <Typography variant="caption" color="text.secondary">{metric.label}</Typography>
                                                                            <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.2 }}>{metric.value}</Typography>
                                                                        </Box>
                                                                    ))}
                                                                </Box>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        ) : null}
                                    </Box>
                                ) : (
                                    <Card sx={sectionCardSx}>
                                        <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                                            <Typography variant="body2" color="text.secondary">
                                                No cross-channel performance data was returned for this range.
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                )}
                            </TabPanel>

                            <TabPanel value={tabIndex} index={2}>
                                <Box sx={{ display: 'grid', gap: 1.25 }}>
                                    <Card sx={sectionCardSx}>
                                        <CardContent sx={{ p: { xs: 1.5, md: 2 }, display: 'grid', gap: 1.2 }}>
                                            <Box>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Cross-Channel Analysis</Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                                                    This tab keeps the structured channel readout only for the viewer application.
                                                </Typography>
                                            </Box>
                                            <AnalysisBulletGroup title="Summary" items={crossChannelAnalysisBlock.summary} theme={theme} />
                                            <AnalysisBulletGroup title="Insights" items={crossChannelAnalysisBlock.insights} theme={theme} tone="success" />
                                            <AnalysisBulletGroup title="Concerns" items={[...crossChannelAnalysisBlock.concerns, ...crossChannelInsights.negativeFindings]} theme={theme} tone="warning" />
                                            <AnalysisBulletGroup title="Recommendations" items={crossChannelAnalysisBlock.recommendations} theme={theme} />
                                            <AnalysisBulletGroup title="Connection Warnings" items={crossChannelInsights.warnings} theme={theme} tone="warning" />
                                        </CardContent>
                                    </Card>
                                </Box>
                            </TabPanel>
                        </>
                    ) : null}
                </Box>
            </Box>
            <ReportQuestionAssistant
                reportType="instant_analysis"
                reportTitle="Instant Analysis"
                profile={reportProfile}
                contextPayload={instantAssistantConfig}
            />
        </Box>
    );
};

export default InstantAnalysis;
